/**
 * File: useContractsQueries.ts
 * Path: apps/admin/src/shared/hooks/useContractsQueries.ts
 * Description: React Query wrappers over the contracts repository.
 */

import { useQuery } from "@tanstack/react-query";
import {
  CONTRACT_COLLECTIONS,
} from "@idk/config";
import {
  contractsRepo,
} from "../services/contractsService";

export const contractsKey = [CONTRACT_COLLECTIONS.contracts] as const;
const listKey = [...contractsKey, "list"] as const;
const forKey = (field: string, id: string) =>
  [...contractsKey, "for", field, id] as const;
const detailKey = (id: string) => [...contractsKey, "detail", id] as const;
export const eventsKey = (id: string) => [...contractsKey, "events", id] as const;
const receiptsKey = (id: string) => [...contractsKey, "receipts", id] as const;
const dueDatesKey = (id: string) => [...contractsKey, "dueDates", id] as const;
export const remindersKey = (id: string) => [...contractsKey, "paymentReminders", id] as const;
export const settingsKey = [CONTRACT_COLLECTIONS.settings] as const;
export const useContractsList = () =>
  useQuery({ queryKey: listKey, queryFn: contractsRepo.list });

export const useContractsFor = (
  field: "clientId" | "leadId" | "jobId",
  id: string | undefined,
) =>
  useQuery({
    queryKey: forKey(field, id ?? ""),
    queryFn: () => contractsRepo.listFor(field, id as string),
    enabled: Boolean(id),
  });

export const useContract = (id: string | undefined) =>
  useQuery({
    queryKey: detailKey(id ?? ""),
    queryFn: () => contractsRepo.get(id as string),
    enabled: Boolean(id),
  });

export const useContractEvents = (id: string | undefined) =>
  useQuery({
    queryKey: eventsKey(id ?? ""),
    queryFn: () => contractsRepo.events(id as string),
    enabled: Boolean(id),
  });

/** The drawn signatures, read by the server (Storage is server-only). */
export const useContractSignatures = (id: string, enabled: boolean) =>
  useQuery({
    queryKey: [...contractsKey, "signatures", id] as const,
    queryFn: () => contractsRepo.signatures(id),
    enabled,
    // A signature never changes once drawn.
    staleTime: Infinity,
  });

/**
 * Every contract's receipts, due dates and reminders, read once. Today,
 * Projects and Money owed add up many contracts, and used to run three
 * queries per contract. Starts with the contracts key, so every payment
 * change that refreshes the contracts refreshes this too.
 */
export const allPaymentsKey = [...contractsKey, "payments", "all"] as const;
export const useAllContractPayments = () =>
  useQuery({ queryKey: allPaymentsKey, queryFn: contractsRepo.allPayments });

/** Shared with the client page, which adds up several contracts' receipts. */
export const contractReceiptsQuery = (id: string) => ({
  queryKey: receiptsKey(id),
  queryFn: () => contractsRepo.receipts(id),
});

export const useContractReceipts = (id: string | undefined) =>
  useQuery({
    ...contractReceiptsQuery(id ?? ""),
    enabled: Boolean(id),
  });

/** Due dates staff set by hand; shared with the money owed page and the client page. */
export const contractDueDatesQuery = (id: string) => ({
  queryKey: dueDatesKey(id),
  queryFn: () => contractsRepo.dueDates(id),
});

/** WhatsApp reminders logged for a contract's payments. */
export const contractRemindersQuery = (id: string) => ({
  queryKey: remindersKey(id),
  queryFn: () => contractsRepo.paymentReminders(id),
});

export const useContractDueDates = (id: string | undefined) =>
  useQuery({
    ...contractDueDatesQuery(id ?? ""),
    enabled: Boolean(id),
  });

export const useContractSettings = () =>
  useQuery({ queryKey: settingsKey, queryFn: contractsRepo.getSettings });
